// Fixture data for the Mix & Apply page (Layer 1 / Data Layer).
// MixRecipe is served live from mix-recipe-api — only Application, TankState,
// and WeatherReading remain as fixtures per Carl's 2026-04-12T21:58Z clarification.

type UsState =
    | 'AL'
    | 'AK'
    | 'AZ'
    | 'AR'
    | 'CA'
    | 'CO'
    | 'CT'
    | 'DE'
    | 'DC'
    | 'FL'
    | 'GA'
    | 'HI'
    | 'ID'
    | 'IL'
    | 'IN'
    | 'IA'
    | 'KS'
    | 'KY'
    | 'LA'
    | 'ME'
    | 'MD'
    | 'MA'
    | 'MI'
    | 'MN'
    | 'MS'
    | 'MO'
    | 'MT'
    | 'NE'
    | 'NV'
    | 'NH'
    | 'NJ'
    | 'NM'
    | 'NY'
    | 'NC'
    | 'ND'
    | 'OH'
    | 'OK'
    | 'OR'
    | 'PA'
    | 'RI'
    | 'SC'
    | 'SD'
    | 'TN'
    | 'TX'
    | 'UT'
    | 'VT'
    | 'VA'
    | 'WA'
    | 'WV'
    | 'WI'
    | 'WY';

type ApplicationMethod = 'AERIAL_DRONE' | 'GROUND_BOOM' | 'CHEMIGATION' | 'OTHER';

type ApplicatorType = 'PRIVATE' | 'COMMERCIAL' | 'NONCOMMERCIAL';

type CarrierType = 'Water' | 'UAN 28' | 'UAN 32' | 'Liquid Fert';

type WindDirection = 'N' | 'NE' | 'E' | 'SE' | 'S' | 'SW' | 'W' | 'NW';

type ApplicationFixture = {
    applicationMethod: ApplicationMethod;
    applicatorLicense: string;
    applicatorLicenseState: UsState;
    applicatorName: string;
    applicatorType: ApplicatorType;
    county: string | null;
    crop: string;
    customerAddress: string | null;
    customerName: string | null;
    date: string;
    faaNNumber: string | null;
    fieldName: string;
    id: string;
    locationLegalDescription: string | null;
    state: UsState;
    targetPest: string;
    totalAcres: number;
};

type TankStateFixture = {
    appTankGal: number;
    carrierRateGalPerAc: number;
    carrierType: CarrierType;
    mixTankGal: number;
};

type WeatherReadingFixture = {
    gustMph: number;
    humidityPct: number;
    recordedAt: string;
    temperatureF: number;
    windDirection: WindDirection;
    windSpeedMph: number;
};

// Enriched tank-volume shape for read-only consumers (e.g. the State
// Compliance Modal) that need the derived fields surfaced alongside the
// user-editable TankStateFixture. Derived fields come from
// useMixApplyCalcs — see StateComplianceModal/hooks/useData for the
// composition point.
type TankVolumeSummary = TankStateFixture & {
    acresPerTank: number;
    batches: number;
    totalSprayVolumeGal: number;
};

const MOCK_APPLICATION: ApplicationFixture = {
    applicationMethod: 'AERIAL_DRONE',
    applicatorLicense: 'CA-12345',
    applicatorLicenseState: 'CA',
    applicatorName: 'John Smith',
    applicatorType: 'COMMERCIAL',
    county: 'Fresno',
    crop: 'Almonds',
    customerAddress: '1234 Farm Rd, Fresno, CA 93650',
    customerName: 'Valley Orchards LLC',
    date: '2026-04-13T09:00:00Z',
    faaNNumber: 'N12345AA',
    fieldName: 'North Orchard Block 3',
    id: 'app-12345',
    locationLegalDescription: 'S12-T15S-R20E',
    state: 'CA',
    targetPest: 'Navel orangeworm',
    totalAcres: 100
};

const MOCK_APPLICATION_NY_BLOCKED: ApplicationFixture = {
    ...MOCK_APPLICATION,
    id: 'app-12346',
    state: 'NY'
};

const MOCK_APPLICATION_OR_COMING_SOON: ApplicationFixture = {
    ...MOCK_APPLICATION,
    id: 'app-12347',
    state: 'OR'
};

const MOCK_TANK_STATE: TankStateFixture = {
    appTankGal: 30,
    carrierRateGalPerAc: 20,
    carrierType: 'Water',
    mixTankGal: 100
};

const MOCK_WEATHER_READING: WeatherReadingFixture = {
    gustMph: 8,
    humidityPct: 55,
    recordedAt: '2026-04-13T09:00:00Z',
    temperatureF: 72,
    windDirection: 'SW',
    windSpeedMph: 5
};

export type {
    ApplicationFixture,
    ApplicationMethod,
    ApplicatorType,
    CarrierType,
    TankStateFixture,
    TankVolumeSummary,
    UsState,
    WeatherReadingFixture,
    WindDirection
};

export {
    MOCK_APPLICATION,
    MOCK_APPLICATION_NY_BLOCKED,
    MOCK_APPLICATION_OR_COMING_SOON,
    MOCK_TANK_STATE,
    MOCK_WEATHER_READING
};
